import { Box, Button, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { CurrencyDropdownType, ExtraJobFieldsType, IFieldData, JobPriorityDropdownType, JobTypeDropdownType } from './CreateJob';
import PriorityBadge from './PriorityBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

interface ReadJobTypeProps {
    jobSector: string;
    jobTitle: string;
    jobStatus: string;
    jobDescription: string;
    newFieldsData: IFieldData[];
    jobPostingId: string;
    extraJobFields: ExtraJobFieldsType;
    currencyDropdown: CurrencyDropdownType[];
    jobTypeDropdown: JobTypeDropdownType[];
    jobPriorityDropdown: JobPriorityDropdownType[];
    hirigFlowData: any[];
    handleJobListing: () => void;
}

export const statusColorMap: Record<string, {
    color: string;
    bgColor: string;
    borderColor: string;
}> = {
    "Screening": {
        color: "#3A5BDB",        // Dark Blue Dot
        bgColor: "#EEFAFF",      // Light Blue Background
        borderColor: "#D0DBFF",  // Soft Blue Border
    },
    "On Hold": {
        color: "#B54708",        // Amber Dot
        bgColor: "#FFFAEB",      // Light Yellow Background
        borderColor: "#FEDF89",  // Yellow Border
    },
    "Shortlisted": {
        color: "#067647",        // Dark Green Dot
        bgColor: "#ECFDF3",      // Light Green Background
        borderColor: "#47CD89",  // Mint Border
    },
    "HR Interview": {
        color: "#9747FF",        // Violet Dot
        bgColor: "#F5EEFF",      // Light Purple Background
        borderColor: "#D9C9FF",  // Lavender Border
    },
    "Assessment": {
        color: "#303F9F",        // Indigo Dot
        bgColor: "#E8EAF6",      // Soft Indigo Background
        borderColor: "#C5CAE9",  // Indigo Border
    },
    "Panel Interview": {
        color: "#5925DC",        // Dark Blue-Violet
        bgColor: "#EAF0FB",      // Pale Blue Background
        borderColor: "#D9D6FE",  // Border Blue-Violet
    },
    "Preboarding": {
        color: "#006064",        // Dark Cyan
        bgColor: "#E0F7FA",      // Cyan Background
        borderColor: "#B2DFDB",  // Teal Border
    },
    "Rejected": {
        color: "#B42318",        // Dark Red
        bgColor: "#FEF3F2",      // Light Red
        borderColor: "#FECDCA",  // Vibrant Red
    },
};



const ReadJob: React.FC<ReadJobTypeProps> = ({
    jobSector,
    jobTitle,
    jobStatus,
    jobDescription,
    newFieldsData,
    jobPostingId,
    extraJobFields,
    currencyDropdown,
    jobTypeDropdown,
    jobPriorityDropdown,
    hirigFlowData,
    handleJobListing
}) => {
    const [stages, setStages] = useState<string[]>([])

    useEffect(() => {
        if (hirigFlowData && hirigFlowData.length > 0) {
            const initialStages = hirigFlowData.map((stage: any) => stage.CandidateStatusKey);
            setStages(initialStages);
        }
    }, [hirigFlowData])


    const getCurrencySymbol = (code: string) => {
        const match = currencyDropdown.find((item) => item.CurrencyCode === code);
        return match ? match.CurrencySymbol : code;
    };

    const getCurrencyLocale = (code: string) => {
        const match = currencyDropdown.find((item) => item.CurrencyCode === code);
        return match?.Locale || 'en-IN'; // fallback
    };


    const getJobTypeLabel = (id: number) => {
        const match = jobTypeDropdown.find(
            (item) => item.JobTypeId === id
        );
        return match ? match.JobTypeKey : id;
    };

    const getJobPriorityLabel = (id: number) => {
        const match = jobPriorityDropdown.find(
            (item) => item.JobPriorityId === id
        );
        return match ? match.JobPriorityKey : String(id);
    };

    const formatNumber = (num: any, locale: any) => {
        return new Intl.NumberFormat(locale).format(num);
    };



    return (
        <Box mb='1.6667vw' sx={{ '& .MuiBox-root': { pb: 0, borderRadius: '1.11vw' } }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: '1vw',
                    mb: '1.6667vw'
                }}
            >
                <Typography variant="h5">Job Details</Typography>
                <Button
                    variant="contained"
                    onClick={handleJobListing}

                >
                    <FontAwesomeIcon style={{ marginRight: '0.4vw' }} icon={faArrowLeft} />{'Back to Job Listing'}
                </Button>
            </Box>

            <Box
                mt={2}
                p={3}
                border={1}
                borderRadius={4}
                borderColor="divider"
            >
                <Grid container spacing={2} mb='1.6667vw'>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Job Posting ID: </strong>{jobPostingId}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Job Title: </strong>{jobTitle}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Department: </strong>{jobSector}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Status: </strong>{jobStatus}</Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb='1.6667vw'>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Number of Openings: </strong>{extraJobFields.noOfOpenings}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Hiring Period: </strong>{extraJobFields.hiringStartDate?.split(" ")[0]} to {extraJobFields.hiringEndDate?.split(" ")[0]}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2">
                            <strong>Currency: </strong>
                            {extraJobFields.currencyType ? extraJobFields.currencyType : 'N/A'}
                        </Typography>

                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2">
                            <strong>Salary Range: </strong>
                            {extraJobFields.salaryStartRanage && extraJobFields.salaryEndRange
                                ? `${getCurrencySymbol(extraJobFields.currencyType)} ${formatNumber(extraJobFields.salaryStartRanage, getCurrencyLocale(extraJobFields.currencyType))} - ${getCurrencySymbol(extraJobFields.currencyType)} ${formatNumber(extraJobFields.salaryEndRange, getCurrencyLocale(extraJobFields.currencyType))}`
                                : 'N/A'}
                        </Typography>

                    </Grid>
                </Grid>

                <Grid container spacing={2} mb='1.6667vw'>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Job Type: </strong>{getJobTypeLabel(Number(extraJobFields.jobType))}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2"><strong>Location: </strong>{extraJobFields.location}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2">
                            <strong>Experience: </strong>
                            {Number(extraJobFields.fromExperience) >= 0 && Number(extraJobFields.toExperience)
                                ? `${extraJobFields.fromExperience || 0} - ${extraJobFields.toExperience || 0} years`
                                : Number(extraJobFields.fromExperience)
                                    ? `${extraJobFields.fromExperience} years`
                                    : Number(extraJobFields.toExperience)
                                        ? `${extraJobFields.toExperience} years`
                                        : 'N/A'
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="body2" component="span">
                            <strong>Priority: </strong>
                            <PriorityBadge badgeName={getJobPriorityLabel(Number(extraJobFields.priority))} />
                        </Typography>
                    </Grid>
                </Grid>

                {/* Job Description */}
                <Box my={2} mb='1.6667vw'>
                    <Typography variant="body2" mb={0.5} component="span">Description</Typography>
                    <Box
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: 'background.paper',
                            maxHeight: '35vh', // 👈 Adjust as needed
                            overflowY: 'auto',
                            scrollBehavior: 'smooth',
                            // Optional scroll styling
                            scrollbarWidth: 'thin',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#c1c1c1',
                                borderRadius: '4px',
                            },
                        }}
                        dangerouslySetInnerHTML={{ __html: jobDescription }}
                    />
                </Box>

                {newFieldsData.length > 0 && (
                    <Grid container spacing={1} alignItems="center" mb='1.6667vw' sx={{ pl: 2 }}>
                        {newFieldsData.map((field) => (
                            <Grid item xs={12} key={field.fieldKey}>
                                <Typography variant="body2">
                                    <strong>{field.fieldName}: </strong>
                                    {field.values.join(', ')}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                )}



                {/* Hiring Flow Section */}
                <Typography variant="h6" fontWeight={900} mb='1.6667vw'>Hiring Flow</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: '1.6667vw' }}>
                    {stages.map((step, index) => {
                        const colorObj = statusColorMap[step] || {
                            color: '#000',
                            bgColor: '#fff',
                            borderColor: '#ccc',
                        };

                        return (
                            <Box
                                key={index}
                                sx={{
                                    backgroundColor: colorObj.bgColor,
                                    color: colorObj.color,
                                    // border: `1.5px solid ${colorObj.borderColor}`,
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '9vw',
                                    height: '5vw',
                                }}
                            >
                                {step}
                            </Box>
                        );
                    })}
                </Box>



            </Box>
        </Box>
    );
};

export default ReadJob;
